import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { FunctionComponent, Suspense, useEffect } from "react";

import {
  CenteredLargeSpinner,
  ScreenWidthMinXLarge,
  Spinner,
  Stack,
  Tile,
  useScreenSize
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { PatientNoticesContextProvider } from "@modules/practice/screens/shared-components/patient-notices/PatientNoticesContext.tsx";
import { PatientNoticesModel } from "@modules/practice/screens/shared-components/patient-notices/PatientNoticesModel.ts";
import { TreeViewSize } from "@shared-types/clinical/tree-view.enum.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { UserSetting } from "@stores/user-experience/models/UserSetting.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { When } from "@ui-components/withPerm.tsx";

import {
  MEDIUM_TREE_VIEW_WIDTH,
  SMALL_TREE_VIEW_WIDTH
} from "../types/tree-widths.constants.ts";
import { EncounterDeletedElsewhereDialog } from "./clinical-form/EncounterDeletedElsewhereDialog.tsx";
import { EncounterDiscardDialog } from "./clinical-form/EncounterDiscardDialog.tsx";
import { EncounterExistsPrompt } from "./clinical-form/EncounterExistsPrompt.tsx";
import { EncounterFinalisedElsewhereDialog } from "./clinical-form/EncounterFinalisedElsewhereDialog.tsx";
import { ClinicalRecordNotifications } from "./ClinicalRecordNotifications.tsx";
import { EncounterDock } from "./EncounterDock.tsx";
import { PatientSummaryContextProvider } from "./patient-summary/helpers/PatientSummaryContext.tsx";
import { HorizontalPersonaDetails } from "./patient-summary/HorizontalPersonaDetails.tsx";
import { PatientActionsSidePanel } from "./PatientActionsSidePanel.tsx";
import { ClinicalSidePanel } from "./PatientRecordLazy.tsx";
import { PatientRecordNotesArea } from "./PatientRecordNotesArea.tsx";
import { QuickAccess } from "./quickAccess/QuickAccess.tsx";

const MAIN_WIDTH = "calc(100% - 466px)";
const LARGE_WIDTH = "calc(50% - 8px)";
const MAX_WIDTH = "calc(100% - 48px)";

const getLeftPanelWidth = (sidePanelSize: TreeViewSize | undefined) => {
  if (sidePanelSize === TreeViewSize.Expanded) return LARGE_WIDTH;
  if (sidePanelSize === TreeViewSize.Default) return MAIN_WIDTH;
  return MAX_WIDTH;
};

const getRightPanelWidth = (sidePanelSize: TreeViewSize | undefined) => {
  if (sidePanelSize === TreeViewSize.Expanded) return LARGE_WIDTH;
  if (sidePanelSize === TreeViewSize.Default) return MEDIUM_TREE_VIEW_WIDTH;
  return SMALL_TREE_VIEW_WIDTH;
};

export const PatientRecordDetails: FunctionComponent = observer(() => {
  const { clinical, core, practice } = useStores();
  const { width } = useScreenSize();

  const {
    isViewOnly,
    clinicalRecord,
    patientId,
    encounterId,
    showEncounterExistsPrompt,
    navigateAway
  } = usePatientRecordScreenContext();

  useEffect(() => {
    // if the active record gets removed from the openRecordIds, then close the patient record screen
    if (
      !clinical.ui.openedRecordIds.some(
        x => x.patientId === patientId && x.encounterId === encounterId
      ) &&
      !showEncounterExistsPrompt
    ) {
      navigateAway();
    }
  }, [
    clinical.ui,
    clinical.ui.openedRecordIds.length,
    patientId,
    encounterId,
    showEncounterExistsPrompt,
    navigateAway
  ]);

  useEffect(() => {
    // Reverses the condition discharge if consult is started with a discharged condition
    clinicalRecord.handleAutoReverseDischarge(isViewOnly);
  }, [clinicalRecord, isViewOnly]);

  useEffect(() => {
    // save the active record to clinical.ui so the quicklinks component knows what clinical record is currently open
    runInAction(() => {
      clinical.ui.activeClinicalRecord = clinicalRecord;
    });

    return () => {
      if (clinical.ui.activeClinicalRecord === clinicalRecord) {
        runInAction(() => {
          clinical.ui.activeClinicalRecord = undefined;
        });
      }
    };
  }, [clinical.ui, clinicalRecord]);

  useEffect(() => {
    // listen to screen with changes and collapse clinical side panner
    // when reached ScreenWidthMinXLarge (1024px)

    const patientRecordTab =
      clinical.ui.tabs.getPatientRecordTab(clinicalRecord);

    if (
      width <= ScreenWidthMinXLarge &&
      patientRecordTab?.sidePanelSize !== TreeViewSize.IconsOnly
    ) {
      patientRecordTab?.toggleSidePanel(false, width);
    }
  }, [clinicalRecord, clinical.ui.tabs, width]);

  useEffect(() => {
    // Clear patientClinicalTasksPromise to set again
    // when switch btw clinical record tabs.
    return () => {
      clinicalRecord?.patientClinicalTasksPromise.clear();
    };
  }, [clinicalRecord?.patientClinicalTasksPromise]);

  if (!clinicalRecord) return null;

  const sidePanelSize = clinicalRecord.patientRecordTab?.state.sidePanelSize;

  return (
    <PatientNoticesContextProvider
      value={
        new PatientNoticesModel(practice, {
          patientId,
          withSystemNotices: false
        })
      }
    >
      <Stack verticalFill tokens={{ childrenGap: 8 }}>
        <ClinicalRecordNotifications />

        <Stack
          grow
          horizontal
          tokens={{ childrenGap: 8 }}
          styles={{ root: { width: "100%", height: "90%" } }}
        >
          <Stack.Item
            styles={{
              root: {
                minWidth: getLeftPanelWidth(sidePanelSize)
              }
            }}
            grow
          >
            <Stack
              tokens={{ childrenGap: 8 }}
              styles={{ root: { height: "100%" } }}
              horizontal
            >
              <Stack>
                <When permission={Permission.QuickAccessAllowed}>
                  {!isViewOnly && (
                    <Stack>
                      <QuickAccess />
                    </Stack>
                  )}
                </When>
                <PatientSummaryContextProvider>
                  <PatientActionsSidePanel />
                </PatientSummaryContextProvider>
              </Stack>
              <Stack
                tokens={{ childrenGap: 8 }}
                styles={{ root: { maxHeight: "100%", minHeight: "100%" } }}
                grow
              >
                <Stack>
                  <Tile
                    styles={{
                      root: {
                        minHeight: "64px"
                      }
                    }}
                  >
                    <HorizontalPersonaDetails />
                  </Tile>
                </Stack>

                <Stack
                  styles={{
                    root: {
                      height: "100%",
                      overflowY: "auto"
                    }
                  }}
                  horizontal
                  tokens={{ childrenGap: 8 }}
                >
                  <PatientRecordNotesArea />
                  <Stack.Item
                    styles={{
                      root: {
                        width: getRightPanelWidth(sidePanelSize),
                        minWidth: getRightPanelWidth(sidePanelSize)
                      }
                    }}
                  >
                    <Tile
                      styles={{
                        root: {
                          padding: 0,
                          height: "100%"
                        },
                        content: {
                          flexGrow: 1,
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column",
                          overflowY: "hidden",
                          overflowX: "auto"
                        }
                      }}
                    >
                      <DataFetcher<UserSetting | undefined>
                        key={core.userId}
                        fetch={({ userExperience }) =>
                          userExperience.getCurrentUserSetting()
                        }
                        fallback={<Spinner />}
                      >
                        {() => {
                          return (
                            <Suspense fallback={<CenteredLargeSpinner />}>
                              <ClinicalSidePanel
                                clinicalRecord={clinicalRecord}
                              />
                            </Suspense>
                          );
                        }}
                      </DataFetcher>
                    </Tile>
                  </Stack.Item>
                </Stack>
                <EncounterDock />
              </Stack>
              <EncounterExistsPrompt />
            </Stack>
          </Stack.Item>
        </Stack>
        <EncounterDeletedElsewhereDialog />
        <EncounterDiscardDialog />
        <EncounterFinalisedElsewhereDialog />
      </Stack>
    </PatientNoticesContextProvider>
  );
});
